/**
 * Region groupings for the neighborhood dropdowns — Asana O2-32.
 *
 * These come from Airtable's `Neighborhood Groupings` field, which CAC added on
 * 2026-08-28 and populated on all 28 neighborhoods. Before it existed this file
 * carried a hand-built name-to-region map guessed from Chicago geography; that
 * guesswork is gone. Nothing here decides which neighborhood belongs where any
 * more — it only decides the order the groups appear in.
 *
 * ORDER is ours, not the data's: Airtable gives no ordering for a single-select,
 * and alphabetical would open the list on "Downtown" then jump around the
 * compass. North / South / West with Downtown first reads the way the city is
 * usually described.
 *
 * Anything unrecognised sorts last under its own label rather than vanishing, so
 * a new grouping added in Airtable shows up in the dropdown immediately instead
 * of silently dropping its neighborhoods.
 */

/** The values in Airtable today, in the order they should appear. */
export const REGION_ORDER = ['Downtown', 'North Side', 'West Side', 'South Side'] as const;

const orderOf = (label: string): number => {
    const i = REGION_ORDER.indexOf(label as (typeof REGION_ORDER)[number]);
    return i === -1 ? REGION_ORDER.length : i;
};

/** Airtable single-selects arrive as a string; a multi-select would be an array. */
export const regionOf = (supplied?: unknown): string => {
    const value = Array.isArray(supplied) ? supplied[0] : supplied;
    const text = String(value ?? '').trim();
    return text || 'Other';
};

/**
 * Takes `{ id, name, region }` options and returns `{ label, options }` groups.
 * Groups with nothing in them never appear, so a dropdown built from a filtered
 * set does not show an empty heading. Within a group, neighborhoods are
 * alphabetical.
 */
export const groupNeighborhoods = <T extends { name?: string; region?: unknown }>(
    items: T[],
): { label: string; options: T[] }[] => {
    const buckets = new Map<string, T[]>();

    for (const item of items || []) {
        const label = regionOf(item?.region);
        if (!buckets.has(label)) buckets.set(label, []);
        buckets.get(label)!.push(item);
    }

    return [...buckets.entries()]
        .sort((a, b) => orderOf(a[0]) - orderOf(b[0]) || a[0].localeCompare(b[0]))
        .map(([label, options]) => ({
            label,
            options: [...options].sort((a, b) =>
                String(a?.name ?? '').localeCompare(String(b?.name ?? ''))),
        }));
};
